import { Key, memo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AdminFormSection, ConditionalRender, AdminFormRow } from '@ui';
import { EMPLOYEE_ORGAN_LIST_LEGACY } from '@lp/features/admin/forms/config';
import { EmployeeFormValues } from '@/types';
import { EmployeeRole } from '@/api/apis/resources';
import { OrganType } from '@/api/apis/case';
import { MultiSelectWithChipsRow } from '../inputs/MultiSelectWithChipsRow';
import { CsOrganCertificationRow } from '../inputs/CsOrganCertificationRow';
import { EmployeeTrialProgramsSelector } from '../inputs/EmployeeTrialProgramsSelector';
import { EmployeeRoleSelector } from '../inputs/EmployeeRoleSelector';
import { rolesWithOrganField } from './constants';

type RoleSpecificationSectionProps = {
  isReadOnly?: boolean;
};

export const RoleSpecificationSection = memo(({ isReadOnly }: RoleSpecificationSectionProps) => {
  const { control, setValue } = useFormContext<EmployeeFormValues>();

  const [roles] = useWatch({
    control,
    name: ['roles'],
  });

  const hasRole = (role: EmployeeRole) => roles?.includes(role);

  const isSpecialist = hasRole(EmployeeRole.ClinicalSpecialist);
  const isClinicalLead = hasRole(EmployeeRole.ClinicalLead);
  const isOMS = hasRole(EmployeeRole.OrganManagementSpecialist);
  const isScheduleManagerRs = hasRole(EmployeeRole.ScheduleManagerRs);

  const isOrganFieldRequired = rolesWithOrganField.some(hasRole);
  const isOrganTypeHide = (isClinicalLead || isOMS) && roles?.length === 1;

  const onOrgansChange = (keys: Key[]) => {
    const stringKeys = keys.map((key) => String(key));
    const hasOrgan = (organ: OrganType) => stringKeys.includes(organ);
    if (!hasOrgan(OrganType.Liver)) {
      setValue('liverSurgeonRegionId', '');
    }
    if (!hasOrgan(OrganType.Lungs) && !hasOrgan(OrganType.Heart)) {
      setValue('heartLungsSurgeonRegionId', '');
    }
  };

  return (
    <AdminFormSection title="Role Specification">
      <EmployeeRoleSelector isReadOnly={isReadOnly} />
      <ConditionalRender show={!isOrganTypeHide}>
        <MultiSelectWithChipsRow
          name="organType"
          title="Organ"
          ariaLabel="Organ"
          placeholder="Select an organ"
          items={EMPLOYEE_ORGAN_LIST_LEGACY}
          isDisabled={isReadOnly}
          isRequired={isOrganFieldRequired}
          onSelectionChangeExtra={onOrgansChange}
        />
      </ConditionalRender>

      <ConditionalRender show={isSpecialist}>
        <AdminFormRow title="Trial Programs">
          <EmployeeTrialProgramsSelector isReadOnly={isReadOnly} />
        </AdminFormRow>
        <CsOrganCertificationRow
          name="shadowOrganSpecializations"
          title="CS Shadow"
          items={EMPLOYEE_ORGAN_LIST_LEGACY}
          isDisabled={isReadOnly}
        />
        <CsOrganCertificationRow
          name="proctorOrganSpecializations"
          title="CS Proctor"
          items={EMPLOYEE_ORGAN_LIST_LEGACY}
          isDisabled={isReadOnly}
        />
      </ConditionalRender>
      <ConditionalRender show={isClinicalLead}>
        <MultiSelectWithChipsRow
          name="phoneSupportOrganSpecializations"
          title="Clinical Lead Organ"
          ariaLabel="Clinical Lead Organ"
          placeholder="Select an organ"
          items={EMPLOYEE_ORGAN_LIST_LEGACY}
          isDisabled={isReadOnly}
          isRequired={isClinicalLead}
          onSelectionChangeExtra={onOrgansChange}
        />
      </ConditionalRender>
      <ConditionalRender show={isScheduleManagerRs}>
        <MultiSelectWithChipsRow
          name="rsScheduleManagerOrgans"
          title="Schedule Manager RS Organ"
          ariaLabel="Schedule Manager RS Organ"
          placeholder="Select an organ"
          items={EMPLOYEE_ORGAN_LIST_LEGACY}
          isDisabled={isReadOnly}
          isRequired={isScheduleManagerRs}
          onSelectionChangeExtra={onOrgansChange}
        />
      </ConditionalRender>
      <ConditionalRender show={isOMS}>
        <MultiSelectWithChipsRow
          name="organManagementSpecialistSpecializations"
          title="OMS Organ"
          ariaLabel="OMS Organ"
          placeholder="Select an organ"
          items={EMPLOYEE_ORGAN_LIST_LEGACY}
          helperText="OMS role requires these certifications to be held"
          isDisabled
          isRequired={isOMS}
          onSelectionChangeExtra={onOrgansChange}
        />
      </ConditionalRender>
    </AdminFormSection>
  );
});
